import { Button, Spinner } from '@tempmee/react';
import { useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { Box, Column, ConfirmPopup } from '~/commonComponents';
import {
  CandidateSortableField,
  CandidateStatus,
  ICandidateListViewEntity,
  useGetCandidateListInfinite,
} from '~/domain/PermanentJobs';
import { CandidateListItem } from './CandidateListItem';

interface ISelectCandidatesPopupProps {
  jobPostingId: string;
  onClose: () => void;
  onConfirm: (candidateIds: string[]) => void;
  defaultSelectedCandidateIds?: string[];
}

export const SelectCandidatesPopup = ({
  jobPostingId,
  onClose,
  onConfirm,
  defaultSelectedCandidateIds = [],
}: ISelectCandidatesPopupProps) => {
  const [selectedCandidates, setSelectedCandidates] = useState<string[]>(
    defaultSelectedCandidateIds,
  );

  // eligible candidates = status: hired + interviewed
  const {
    data: candidates,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useGetCandidateListInfinite(
    {
      jobPostingId,
      status: [CandidateStatus.Hired, CandidateStatus.Interviewed],
      orderBy: CandidateSortableField.Hired,
    },
    { enabled: !!jobPostingId },
  );

  // Infinite scroll trigger
  const { ref: loadMoreRef, inView } = useInView();

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Auto-select hired candidates on data load
  useEffect(() => {
    const hiredIds = candidates
      .filter((c) => c.officeStatus === CandidateStatus.Hired)
      .map((c) => c.id);

    // Only update if there are new hired candidates not already selected
    const newHiredIds = hiredIds.filter((id) => !selectedCandidates.includes(id));

    if (newHiredIds.length > 0) {
      setSelectedCandidates((prev) => [...new Set([...prev, ...newHiredIds])]);
    }
  }, [candidates, selectedCandidates]);

  const handleToggleCandidate = (candidate: ICandidateListViewEntity) => {
    // Prevent toggling hired candidates - they must remain selected
    if (candidate.officeStatus === CandidateStatus.Hired) {
      return;
    }

    setSelectedCandidates((prev) =>
      prev.includes(candidate.id)
        ? prev.filter((id) => id !== candidate.id)
        : [...prev, candidate.id],
    );
  };

  const hasAnyHiredCandidates = candidates.some((c) => c.officeStatus === CandidateStatus.Hired);

  return (
    <ConfirmPopup
      title="Before You Close This Job"
      description="If you hired through GoTu, select your new team member(s) below. Otherwise, simply close this job to finish."
      descriptionStyle={{ marginBottom: 22 }}
      closePopup={onClose}
      dismissiblePopup={false}
      showClosePopupIcon
    >
      <Box
        width={'80%'}
        overflowY="auto"
        marginBottom={22}
        paddingInline={22}
        style={{
          maxHeight: 248,
          scrollbarWidth: 'thin',
        }}
      >
        {isLoading && candidates.length === 0 && <Spinner centered variant="default" size="md" />}

        {candidates.map((candidate) => (
          <CandidateListItem
            key={candidate.id}
            id={candidate.id}
            name={candidate.professional.name}
            isSelected={selectedCandidates.includes(candidate.id)}
            disabled={candidate.officeStatus === CandidateStatus.Hired}
            onToggle={() => handleToggleCandidate(candidate)}
          />
        ))}

        {/* Infinite scroll trigger */}
        {hasNextPage && (
          <div ref={loadMoreRef} style={{ padding: 8, textAlign: 'center' }}>
            {isFetchingNextPage && <Spinner centered variant="default" size="md" />}
          </div>
        )}
      </Box>
      <Column>
        <Button
          onClick={() => onConfirm(selectedCandidates)}
          disabled={selectedCandidates.length === 0}
          size="md"
        >
          {`Confirm ${selectedCandidates.length || ''} Hires & Close Job`}
        </Button>
        {!hasAnyHiredCandidates && (
          <Button
            variant="secondary"
            size="lg"
            style={{ marginTop: 16 }}
            onClick={() => onConfirm([])}
          >
            Close Job
          </Button>
        )}
      </Column>
    </ConfirmPopup>
  );
};
